import React, { useState, useEffect } from 'react';
import {
    Briefcase, Users, MapPin, Clock, ChevronRight, ArrowLeft, Plus,
    XCircle, Trash2, Search, CheckCircle, Building2, ChevronDown
} from 'lucide-react';
import api from '../services/api';

const PIPELINE_STAGES = [
    'Interview waiting', 'Interview Scheduled', 'Interview attended',
    'Offer Received', 'Visa submitted', 'Visa Granted', 'Migrated'
];

const STAGE_COLORS = {
    'Interview waiting': { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
    'Interview Scheduled': { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', dot: 'bg-cyan-500' },
    'Interview attended': { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', dot: 'bg-teal-500' },
    'Offer Received': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
    'Visa submitted': { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', dot: 'bg-violet-500' },
    'Visa Granted': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500' },
    'Migrated': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', dot: 'bg-green-500' },
};

const STATUS_BADGE = {
    open: 'bg-green-100 text-green-700',
    closed: 'bg-red-100 text-red-700',
    draft: 'bg-gray-100 text-gray-600',
};

const StageBadge = ({ stage }) => {
    const colors = STAGE_COLORS[stage] || { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', dot: 'bg-gray-500' };
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${colors.bg} ${colors.text} border ${colors.border}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${colors.dot}`}></span>
            {stage}
        </span>
    );
};

// ─── Assign Candidates Modal ───────────────────────────────
const AssignCandidatesModal = ({ jobId, recruiterId, onClose, onAssigned, showToast }) => {
    const [search, setSearch] = useState('');
    const [candidates, setCandidates] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchCandidates = async () => {
            setLoading(true);
            try {
                let url = `/staff/users?role=candidate&approval_status=approved&search=${search}&limit=50`;
                if (recruiterId) {
                    url += `&recruiter_id=${recruiterId}`;
                }
                const res = await api.get(url);
                setCandidates(res.data.users || []);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        const debounce = setTimeout(fetchCandidates, 300);
        return () => clearTimeout(debounce);
    }, [search]);

    const toggleSelect = (id) => {
        setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleAssign = async () => {
        if (selected.length === 0) return;
        try {
            await api.post(`/staff/recruiting-jobs/${jobId}/candidates`, { candidate_ids: selected });
            showToast(`${selected.length} candidate(s) assigned`, 'success');
            onAssigned();
            onClose();
        } catch (e) {
            showToast('Failed to assign candidates', 'error');
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-slate-900">Assign Candidates to Job</h3>
                    <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                        <XCircle className="h-5 w-5" />
                    </button>
                </div>
                <div className="p-4 border-b border-slate-100">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-900 bg-white focus:ring-2 focus:ring-cyan-500 outline-none placeholder-slate-400"
                            placeholder="Search candidates by name or email..." autoFocus />
                    </div>
                    {selected.length > 0 && (
                        <p className="text-xs text-cyan-600 font-medium mt-2">{selected.length} candidate(s) selected</p>
                    )}
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {loading ? (
                        <p className="text-center text-sm text-slate-400 py-6">Searching...</p>
                    ) : candidates.filter(c => c.role === 'candidate').length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-sm font-medium text-slate-600">No candidates found</p>
                            {recruiterId ? (
                                <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">
                                    {search ? `No provisioned candidates match "${search}".` : 'No candidates are provisioned to this recruiter yet. Go to Staff → Recruiter Visibility to provision candidates.'}
                                </p>
                            ) : (
                                <p className="text-xs text-slate-400 mt-1">No approved candidates available.</p>
                            )}
                        </div>
                    ) : candidates.filter(c => c.role === 'candidate').map(c => (
                        <div key={c.id} onClick={() => toggleSelect(c.id)}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${selected.includes(c.id) ? 'border-cyan-400 bg-cyan-50 ring-1 ring-cyan-200' : 'border-slate-200 hover:border-cyan-200 hover:bg-slate-50'
                                }`}>
                            {c.photo_url ? (
                                <img src={c.photo_url} alt="" className="h-9 w-9 rounded-full object-cover" />
                            ) : (
                                <div className="h-9 w-9 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 font-bold text-sm">{c.name?.charAt(0)}</div>
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm text-slate-900 truncate">{c.name}</p>
                                <p className="text-xs text-slate-500">{c.qualification || 'N/A'} • {c.german_level || 'N/A'}</p>
                            </div>
                            {selected.includes(c.id) && <CheckCircle className="h-5 w-5 text-cyan-600 flex-shrink-0" />}
                        </div>
                    ))}
                </div>
                <div className="p-4 border-t border-slate-100 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                    <button onClick={handleAssign} disabled={selected.length === 0}
                        className="px-5 py-2 text-sm font-semibold text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 disabled:opacity-50 shadow-sm">
                        Assign {selected.length > 0 ? `(${selected.length})` : ''}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Reject Modal ───────────────────────────────
const RejectModal = ({ candidateName, onConfirm, onClose }) => {
    const [reason, setReason] = useState('');
    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Reject Candidate</h3>
                <p className="text-sm text-slate-500 mb-4">This will move <strong>{candidateName}</strong> to the rejected list.</p>
                <textarea value={reason} onChange={e => setReason(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-red-400 outline-none resize-none"
                    rows={3} placeholder="Reason for rejection (optional)..." />
                <div className="flex justify-end gap-3 mt-4">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                    <button onClick={() => onConfirm(reason)} className="px-5 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 shadow-sm">Reject</button>
                </div>
            </div>
        </div>
    );
};

// ─── Job Detail — Staff View (full pipeline management) ────────
const StaffJobDetail = ({ job, onBack, showToast }) => {
    const [candidates, setCandidates] = useState([]);
    const [rejected, setRejected] = useState([]);
    const [showAssign, setShowAssign] = useState(false);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [viewMode, setViewMode] = useState('pipeline'); // pipeline | rejected

    const fetchCandidates = async () => {
        try {
            const res = await api.get(`/staff/recruiting-jobs/${job.id}/candidates`);
            setCandidates(res.data.candidates || []);
        } catch (e) { console.error(e); }
    };

    const fetchRejected = async () => {
        try {
            const res = await api.get(`/staff/recruiting-jobs/${job.id}/rejected`);
            setRejected(res.data.rejected || []);
        } catch (e) { console.error(e); }
    };

    useEffect(() => { fetchCandidates(); fetchRejected(); }, [job.id]);

    const handleStageChange = async (candidateId, newStage) => {
        try {
            await api.put(`/staff/recruiting-jobs/${job.id}/candidates/${candidateId}/stage`, { stage: newStage });
            showToast(`Candidate moved to "${newStage}"`, 'success');
            fetchCandidates();
        } catch (e) {
            showToast('Failed to update stage', 'error');
        }
    };

    const handleReject = async (reason) => {
        if (!rejectTarget) return;
        try {
            await api.put(`/staff/recruiting-jobs/${job.id}/candidates/${rejectTarget.id}/stage`, {
                stage: 'Rejected', rejection_reason: reason
            });
            showToast('Candidate rejected', 'success');
            setRejectTarget(null);
            fetchCandidates();
            fetchRejected();
        } catch (e) {
            showToast('Failed to reject candidate', 'error');
        }
    };

    const handleRemoveFromJob = async (candidateId) => {
        if (!window.confirm('Remove this candidate from the job?')) return;
        try {
            await api.delete(`/staff/recruiting-jobs/${job.id}/candidates/${candidateId}`);
            showToast('Candidate removed', 'success');
            fetchCandidates();
        } catch (e) {
            showToast('Failed to remove candidate', 'error');
        }
    };

    const handleRemoveFromRejected = async (rejectedId) => {
        if (!window.confirm('Remove from rejected list?')) return;
        try {
            await api.delete(`/staff/recruiting-jobs/rejected/${rejectedId}`);
            showToast('Removed from rejected list', 'success');
            fetchRejected();
        } catch (e) {
            showToast('Failed to remove', 'error');
        }
    };

    // group by stage
    const stageGroups = {};
    PIPELINE_STAGES.forEach(s => { stageGroups[s] = []; });
    candidates.forEach(c => {
        if (stageGroups[c.stage]) stageGroups[c.stage].push(c);
        else stageGroups['Interview waiting'].push(c);
    });

    return (
        <div className="space-y-6 animate-fadeIn">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button onClick={onBack} className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors">
                    <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="flex-1">
                    <h2 className="text-xl font-bold text-slate-900">{job.title}</h2>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        {job.company_name && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{job.company_name}</span>}
                        {job.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{job.location}</span>}
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" />{candidates.length} active</span>
                        <span className="flex items-center gap-1 text-red-500"><XCircle className="h-3 w-3" />{rejected.length} rejected</span>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setViewMode('pipeline')}
                        className={`px-3.5 py-2 text-xs font-semibold rounded-lg transition-all ${viewMode === 'pipeline' ? 'bg-cyan-600 text-white shadow-sm' : 'text-slate-600 bg-slate-100 hover:bg-slate-200'}`}>
                        Pipeline
                    </button>
                    <button onClick={() => setViewMode('rejected')}
                        className={`px-3.5 py-2 text-xs font-semibold rounded-lg transition-all ${viewMode === 'rejected' ? 'bg-red-600 text-white shadow-sm' : 'text-slate-600 bg-slate-100 hover:bg-slate-200'}`}>
                        Rejected ({rejected.length})
                    </button>
                    <button onClick={() => setShowAssign(true)}
                        className="flex items-center gap-1.5 px-4 py-2 bg-cyan-600 text-white rounded-lg text-xs font-semibold hover:bg-cyan-700 transition-all shadow-sm">
                        <Plus className="h-3.5 w-3.5" /> Assign Candidates
                    </button>
                </div>
            </div>

            {/* Pipeline View */}
            {viewMode === 'pipeline' && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    {/* Stage summary bar */}
                    <div className="px-6 py-3 border-b border-slate-100 flex gap-1 overflow-x-auto bg-slate-50/50">
                        {PIPELINE_STAGES.map(stage => {
                            const count = stageGroups[stage]?.length || 0;
                            const colors = STAGE_COLORS[stage] || {};
                            return (
                                <div key={stage} className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold ${count > 0 ? `${colors.bg} ${colors.text}` : 'bg-white text-slate-400 border border-slate-100'}`}>
                                    <span className={`h-4 w-4 rounded-full flex items-center justify-center text-[9px] font-bold ${count > 0 ? `${colors.dot} text-white` : 'bg-slate-200'}`}>{count}</span>
                                    {stage}
                                </div>
                            );
                        })}
                    </div>

                    {/* Candidate cards */}
                    <div className="p-6 space-y-5">
                        {PIPELINE_STAGES.filter(s => stageGroups[s].length > 0).map(stage => (
                            <div key={stage}>
                                <div className="flex items-center gap-2 mb-3">
                                    <StageBadge stage={stage} />
                                    <span className="text-xs text-slate-400">({stageGroups[stage].length})</span>
                                </div>
                                <div className="space-y-2">
                                    {stageGroups[stage].map(c => (
                                        <div key={c.id} className="flex items-center gap-4 p-3.5 bg-slate-50 rounded-lg border border-slate-200 hover:shadow-sm transition-shadow">
                                            {c.photo_url ? (
                                                <img src={c.photo_url} alt="" className="h-10 w-10 rounded-full object-cover border border-slate-200" />
                                            ) : (
                                                <div className="h-10 w-10 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 font-bold text-sm">{c.name?.charAt(0)}</div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold text-sm text-slate-900">{c.name}</p>
                                                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-500">
                                                    {c.email && <span>{c.email}</span>}
                                                    {c.qualification && <span>• {c.qualification}</span>}
                                                    {c.german_level && <span>• {c.german_level}</span>}
                                                </div>
                                            </div>
                                            {/* Stage selector */}
                                            <div className="relative">
                                                <select value={c.stage} onChange={e => handleStageChange(c.id, e.target.value)}
                                                    className="appearance-none bg-white border border-slate-200 rounded-lg px-3 py-1.5 pr-8 text-xs font-medium text-slate-700 focus:ring-2 focus:ring-cyan-400 outline-none cursor-pointer">
                                                    {PIPELINE_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                                            </div>
                                            <button onClick={() => setRejectTarget(c)}
                                                className="px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
                                                Reject
                                            </button>
                                            <button onClick={() => handleRemoveFromJob(c.id)}
                                                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {candidates.length === 0 && (
                            <div className="text-center py-12 text-slate-400">
                                <Users className="h-10 w-10 mx-auto mb-3 opacity-40" />
                                <p className="font-medium">No candidates assigned</p>
                                <p className="text-xs mt-1">Click "Assign Candidates" to add candidates to this job.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Rejected View */}
            {viewMode === 'rejected' && (
                <div className="bg-white rounded-xl border border-red-100 shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-red-50 bg-red-50/50">
                        <h3 className="font-bold text-red-700">Rejected Candidates</h3>
                    </div>
                    <div className="p-4 space-y-2">
                        {rejected.length === 0 ? (
                            <p className="text-center py-8 text-sm text-slate-400">No rejected candidates</p>
                        ) : rejected.map(rc => (
                            <div key={rc.id} className="flex items-center gap-3 p-3.5 bg-red-50/30 rounded-lg border border-red-100">
                                {rc.photo_url ? (
                                    <img src={rc.photo_url} alt="" className="h-9 w-9 rounded-full object-cover" />
                                ) : (
                                    <div className="h-9 w-9 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold text-xs">{rc.name?.charAt(0)}</div>
                                )}
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-slate-800">{rc.name}</p>
                                    <p className="text-xs text-slate-500">{rc.email}</p>
                                    {rc.previous_stage && <p className="text-[11px] text-slate-400 mt-0.5">Was at: {rc.previous_stage}</p>}
                                    {rc.rejection_reason && <p className="text-[11px] text-red-500">Reason: {rc.rejection_reason}</p>}
                                    {rc.rejected_by_name && <p className="text-[10px] text-slate-400 mt-0.5">By: {rc.rejected_by_name} • {new Date(rc.rejected_at).toLocaleDateString()}</p>}
                                </div>
                                <button onClick={() => handleRemoveFromRejected(rc.id)}
                                    className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                                    Remove
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Modals */}
            {showAssign && (
                <AssignCandidatesModal jobId={job.id} recruiterId={job.recruiter_id} onClose={() => setShowAssign(false)} onAssigned={fetchCandidates} showToast={showToast} />
            )}
            {rejectTarget && (
                <RejectModal candidateName={rejectTarget.name} onConfirm={handleReject} onClose={() => setRejectTarget(null)} />
            )}
        </div>
    );
};

// ─── Main Staff Jobs Tab ────────────────────────────────────
const StaffJobsTab = ({ showToast }) => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);

    const fetchJobs = async () => {
        setLoading(true);
        try {
            const res = await api.get('/staff/recruiting-jobs');
            setJobs(res.data.jobs || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchJobs(); }, []);

    if (selected) {
        return <StaffJobDetail job={selected} onBack={() => { setSelected(null); fetchJobs(); }} showToast={showToast} />;
    }

    return (
        <div className="space-y-6 animate-fadeIn">
            <div>
                <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                    <Briefcase className="h-6 w-6 text-cyan-600" /> Recruiting Job Postings
                </h2>
                <p className="text-slate-500 mt-1">Manage candidate assignments and interview pipeline for recruiter job postings.</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                    <p className="text-2xl font-bold text-slate-900">{jobs.length}</p>
                    <p className="text-xs text-slate-500">Total Jobs</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                    <p className="text-2xl font-bold text-green-600">{jobs.filter(j => j.status === 'open').length}</p>
                    <p className="text-xs text-slate-500">Open</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                    <p className="text-2xl font-bold text-cyan-600">{jobs.reduce((s, j) => s + (j.candidate_count || 0), 0)}</p>
                    <p className="text-xs text-slate-500">Assigned Candidates</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                    <p className="text-2xl font-bold text-red-500">{jobs.reduce((s, j) => s + (j.rejected_count || 0), 0)}</p>
                    <p className="text-xs text-slate-500">Rejected</p>
                </div>
            </div>

            {/* Job List */}
            {loading ? (
                <div className="text-center py-16 text-slate-400">Loading jobs...</div>
            ) : jobs.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm text-center py-16">
                    <Briefcase className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p className="font-semibold text-slate-600">No job postings yet</p>
                    <p className="text-xs text-slate-400 mt-1">Recruiting partners will create job postings which will appear here.</p>
                </div>
            ) : (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-6 py-3">Job Title</th>
                                <th className="text-left px-4 py-3">Company</th>
                                <th className="text-left px-4 py-3">Location</th>
                                <th className="text-center px-4 py-3">Positions</th>
                                <th className="text-center px-4 py-3">Candidates</th>
                                <th className="text-center px-4 py-3">Rejected</th>
                                <th className="text-center px-4 py-3">Status</th>
                                <th className="text-right px-6 py-3">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {jobs.map(job => (
                                <tr key={job.id} className="hover:bg-cyan-50/30 cursor-pointer transition-colors" onClick={() => setSelected(job)}>
                                    <td className="px-6 py-4">
                                        <p className="font-semibold text-sm text-slate-900">{job.title}</p>
                                        {job.employment_type && <p className="text-xs text-slate-500 capitalize">{job.employment_type}</p>}
                                    </td>
                                    <td className="px-4 py-4 text-sm text-slate-700">{job.company_name || job.recruiter_name || '—'}</td>
                                    <td className="px-4 py-4 text-sm text-slate-500">{job.location || '—'}</td>
                                    <td className="px-4 py-4 text-center text-sm font-semibold text-slate-700">{job.positions_available}</td>
                                    <td className="px-4 py-4 text-center">
                                        <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-cyan-100 text-cyan-700 text-xs font-bold">{job.candidate_count || 0}</span>
                                    </td>
                                    <td className="px-4 py-4 text-center">
                                        {job.rejected_count > 0 ? (
                                            <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-red-100 text-red-600 text-xs font-bold">{job.rejected_count}</span>
                                        ) : <span className="text-slate-300">—</span>}
                                    </td>
                                    <td className="px-4 py-4 text-center">
                                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${STATUS_BADGE[job.status] || ''}`}>{job.status}</span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <ChevronRight className="h-4 w-4 text-slate-400 inline-block" />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default StaffJobsTab;
